/**
 * @file upload-session.schema.ts
 * @description Trilha de auditoria de uploads de vídeo (coleção 'uploadSessions').
 *
 * Cada tentativa de upload vira um documento (id == videoId) que registra cada
 * etapa do pipeline — init, upload no browser, finalize, transcode, webhook,
 * reconciliação — com timestamps, origem e erro exato. É também o índice
 * videoId → courseId usado pelo webhook/reconciler para resolver o vídeo sem
 * varrer todos os cursos.
 */

import { z } from 'zod';

/**
 * Máquina de estados da sessão (monotônica; aplicada em UploadSessionService):
 * INITIATED → UPLOADING → UPLOADED → FINALIZED → TRANSCODING → READY
 * Qualquer estado → FAILED.
 * INITIATED/UPLOADING → ABANDONED (abort do usuário ou varredura de órfãos).
 */
export const UploadSessionStatusEnum = z.enum([
  'INITIATED',
  'UPLOADING',
  'UPLOADED',
  'FINALIZED',
  'TRANSCODING',
  'READY',
  'FAILED',
  'ABANDONED',
]);
export type UploadSessionStatus = z.infer<typeof UploadSessionStatusEnum>;

export const UploadSessionStepEnum = z.enum([
  'INIT',
  'CLIENT_UPLOAD_PROGRESS',
  'CLIENT_UPLOAD_DONE',
  'CLIENT_UPLOAD_FAILED',
  'FINALIZE_REQUESTED',
  'FILE_VERIFIED',
  'COURSE_UPDATED',
  'TRANSCODE_REQUESTED',
  'TRANSCODE_WEBHOOK',
  'RECONCILED',
  'ROLLBACK',
  'BACKFILL',
]);
export type UploadSessionStep = z.infer<typeof UploadSessionStepEnum>;

/**
 * Origem do step. Steps CLIENT_* são reportados pelo browser e valem como
 * telemetria; transições de status após UPLOADED só são feitas por steps
 * observados no servidor (API/TRANSCODER/RECONCILER).
 */
export const UploadSessionStepSourceEnum = z.enum(['CLIENT', 'API', 'TRANSCODER', 'RECONCILER', 'SYSTEM']);
export type UploadSessionStepSource = z.infer<typeof UploadSessionStepSourceEnum>;

export const UploadSessionStepSchema = z.object({
  step: UploadSessionStepEnum,
  at: z.date(),
  source: UploadSessionStepSourceEnum,
  ok: z.boolean(),
  detail: z.string().optional(),
  error: z.string().optional(),
  progressPercent: z.number().min(0).max(100).optional(),
});
export type UploadSessionStepEntry = z.infer<typeof UploadSessionStepSchema>;

export const UploadMethodEnum = z.enum(['SIGNED', 'RESUMABLE']);
export type UploadMethod = z.infer<typeof UploadMethodEnum>;

export const UploadSessionSchema = z.object({
  /** id do documento == videoId (filename sem extensão). */
  id: z.string(),
  videoId: z.string(),
  /** id do documento de conteúdo (curso legado OU vídeo novo com structure). */
  courseId: z.string(),
  // Localização do item no documento — formato legado (modules[].videos[])...
  moduleIndex: z.number().int().min(0).optional(),
  videoIndex: z.number().int().min(0).optional(),
  // ...ou formato novo (structure.items[] / structure.sections[].items[]).
  sectionIndex: z.number().int().min(0).nullable().optional(),
  itemIndex: z.number().int().min(0).optional(),
  filename: z.string(),
  originalName: z.string().optional(),
  sizeBytes: z.number().int().positive().optional(),
  mimeType: z.string().optional(),
  uploadMethod: UploadMethodEnum,
  status: UploadSessionStatusEnum,
  actor: z.object({
    userId: z.string(),
    email: z.string().optional(),
  }),
  transcodingJobId: z.string().optional(),
  /** requestId da requisição de init — correlaciona com o Cloud Logging. */
  requestId: z.string().optional(),
  /** Última mensagem de erro (espelho do step com error mais recente). */
  error: z.string().optional(),
  steps: z.array(UploadSessionStepSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
  /** createdAt + 90 dias — política de TTL do Firestore limpa sessões antigas. */
  expiresAt: z.date(),
});
export type UploadSession = z.infer<typeof UploadSessionSchema>;

/** Transições válidas da máquina de estados (regressões são ignoradas). */
export const UPLOAD_SESSION_STATUS_ORDER: Record<UploadSessionStatus, number> = {
  INITIATED: 0,
  UPLOADING: 1,
  UPLOADED: 2,
  FINALIZED: 3,
  TRANSCODING: 4,
  READY: 5,
  // Terminais fora da progressão linear:
  FAILED: 100,
  ABANDONED: 100,
};

/**
 * Valida uma transição de status: progressão linear só avança (nunca regride);
 * FAILED é permitido a partir de qualquer estado não-terminal; ABANDONED só a
 * partir de INITIATED/UPLOADING.
 */
export function canTransitionUploadSession(
  from: UploadSessionStatus,
  to: UploadSessionStatus
): boolean {
  if (from === to) return false;
  if (from === 'FAILED' || from === 'ABANDONED' || from === 'READY') return false;
  if (to === 'FAILED') return true;
  if (to === 'ABANDONED') return from === 'INITIATED' || from === 'UPLOADING';
  return UPLOAD_SESSION_STATUS_ORDER[to] > UPLOAD_SESSION_STATUS_ORDER[from];
}
